import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import SwipeableDrawer from '@material-ui/core/SwipeableDrawer';
import Button from '@material-ui/core/Button';
import moment from 'moment';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemText from '@material-ui/core/ListItemText';
import PersonIcon from '@material-ui/icons/Person';
import TextField from '@material-ui/core/TextField';
import useTimeline from '../../timeline/useTimeline';
import globals from '../../../../globals/communityGlobals';
import { updateTreatmentInstance } from '../../../../actions/entities';
import { getMovedReasons } from '../../../../actions/areas/planner';
import { DropdownFilter } from '../../../Planner/Filters/DropdownFilter';
import { selectionTypes } from '../../../../enums/treatmentSelectionType';
import { getDateForApi, parseShortDateTime, isValidDate, timeIsBetweenDateRange } from '../../../../utils/dates';
import KeyboardDateSelector from '../../../common/KeyboardDateTimeSelectors/KeyboardDateSelector';
import { getErrorProps } from '../../../../utils/keyboardSelectorFunctions';

const riskMaxLength = 500;
const errors = globals.getArea('error');

export default function RescheduleTreatment({ treatmentDetails, toggleRefresh, includeTreatments }) {
  const originalDate = new Date(treatmentDetails.treatmentInstance.occurrence);
  const [componentState, setComponentState] = useState({
    selectedDate: new Date(treatmentDetails.treatmentInstance.occurrence),
    movedReasons: [],
    movedReason: { id: 0, name: '' },
    riskAssesment: '',
    disableFields: true,
    error: '',
  });
  const [validationErrors, setValidationErrors] = useState({});
  const isMaster = treatmentDetails.treatmentInstance.masterId === null;

  const dispatch = useDispatch();

  const {
    activeUnassignedTreatmentForReschedule,
    setActiveUnassignedTreatmentForReschedule,
    refreshClinicianTreatmentsAndClosePanels,
    getAccessToken,
  } = useTimeline();
  const locale = globals.getArea('treatments');
  const errorsLocale = globals.getArea('error');

  const selectionType = () => {
    if (treatmentDetails.treatment.isSpecific) return selectionTypes.specific;
    if (treatmentDetails.treatment.isMultiple) return selectionTypes.multiple;
    return selectionTypes.timeSlot;
  };

  const recheduleUnassignedTreatmentInstance = () => {
    let updatedTreatment = {};
    const treatmentInstance = treatmentDetails.treatmentInstance;
    if (selectionType() === selectionTypes.specific) {
      updatedTreatment = {
        treatmentId: treatmentInstance.treatmentId,
        treatmentInstanceId: treatmentInstance.treatmentInstanceId,
        isSpecific: true,
        occurenceOriginalDate: getDateForApi(treatmentInstance.occurrence),
        specificTimeFrom: getDateForApi(treatmentInstance.effectiveTimeSlots[0].from),
      };
    } else {
      updatedTreatment = {
        treatmentId: treatmentInstance.treatmentId,
        treatmentInstanceId: treatmentInstance.treatmentInstanceId,
        isSpecific: false,
        occurenceOriginalDate: getDateForApi(treatmentInstance.occurrence),
        timeSlotsMatrix: treatmentInstance.selectedTimeSlots,
        timeSlotPackageId: treatmentInstance.timeSlotPackage.id,
      };
    }
    updatedTreatment.occurenceDate = getDateForApi(componentState.selectedDate);
    updatedTreatment.movedReasonId = componentState.movedReason;
    updatedTreatment.movedRiskAssessment = componentState.riskAssesment;
    dispatch(updateTreatmentInstance(updatedTreatment)).then(() => {
      refreshClinicianTreatmentsAndClosePanels();
      if (includeTreatments) toggleRefresh();
    });
  };

  const handleDateChange = date => {
    setValidationErrors({
      selectedDate: date ? null : errors.getMsg('requiredFieldValidationMessage'),
    });
    setComponentState(prevState => ({
      ...prevState,
      selectedDate: date,
    }));

    if (!isValidDate(date)) {
      return;
    }

    const nowAt = globals.now;
    const valueAt = moment(new Date(date));
    const originalDateAt = moment(originalDate);
    const exactAt = moment(treatmentDetails.treatmentInstance.occurrence);

    const isInPresentOrFutureOrExact =
      valueAt.isSame(nowAt, 'day') || valueAt.isAfter(nowAt) || valueAt.isSame(exactAt, 'day');
    if (!isInPresentOrFutureOrExact) {
      setValidationErrors({
        selectedDate: errorsLocale.getMsg('cannotBeInThePast'),
      });
    }

    const isSelectedDateWithinValidRange = timeIsBetweenDateRange(
      { dateFrom: treatmentDetails.minimumPossibleDate, dateTo: treatmentDetails.maximumPossibleDate },
      date,
    );

    const shouldEnableFields =
      isInPresentOrFutureOrExact && !originalDateAt.isSame(valueAt, 'day') && isSelectedDateWithinValidRange;

    if (shouldEnableFields && componentState.movedReasons.length === 0) {
      getMovedReasons(getAccessToken, {}, false, response => {
        setComponentState(prev => ({
          ...prev,
          disableFields: false,
          movedReasons: response,
          movedReason: response.length > 0 ? response[0].id : null,
          selectedDate: date,
        }));
      });
    } else {
      setComponentState(prev => ({
        ...prev,
        disableFields: !shouldEnableFields,
        selectedDate: date,
      }));
    }
  };

  const isSelectedDateValid = () => {
    const valueAt = moment(new Date(componentState.selectedDate));
    const originalDateAt = moment(originalDate);

    return (
      isValidDate(componentState.selectedDate) &&
      !validationErrors.selectedDate &&
      !originalDateAt.isSame(valueAt, 'day') &&
      timeIsBetweenDateRange(
        { dateFrom: treatmentDetails.minimumPossibleDate, dateTo: treatmentDetails.maximumPossibleDate },
        componentState.selectedDate,
      )
    );
  };

  const shouldDisableButton = () => {
    return (
      !componentState.movedReason ||
      !componentState.riskAssesment ||
      componentState.riskAssesment.length > riskMaxLength ||
      !isSelectedDateValid()
    );
  };

  const headerInfo = () => {
    return (
      <div className="grey-sidePanel-header">
        <div className="sidePanel-header-subtitle">
          <Box flexGrow={1} py={1}>
            <Typography variant="subtitle1">Reschedule visit</Typography>
          </Box>
        </div>
        <div className="sidePanel-header-title-wrap">
          <div className="sidePanel-header-title">
            <Typography variant="h2">{activeUnassignedTreatmentForReschedule.patientName}</Typography>
            <Typography variant="h3" gutterBottom>
              {activeUnassignedTreatmentForReschedule.postcode}
            </Typography>
            <Typography variant="h5">{activeUnassignedTreatmentForReschedule.band}</Typography>
          </div>
          <div className="sidePanel-header-info">
            <div className="sidePanel-header-info patient-header">
              <List component="nav">
                <div className="patient-info-details">
                  <ListItem key={'required-people'}>
                    <ListItemIcon>
                      <PersonIcon />
                    </ListItemIcon>
                    <ListItemText primary={activeUnassignedTreatmentForReschedule.numberOfClinicians} />
                  </ListItem>
                </div>
              </List>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const mainContent = () => {
    return (
      <div className="reschedule-sidePanel-form">
        <Box mb={2} className="reschedule-dateInput">
          <Typography variant="h6" gutterBottom>
            Original Date
          </Typography>
          <List className="list-underline">
            <ListItem>
              <span className="icon-extend icon-date icon-small" />
              <ListItemText secondary={parseShortDateTime(treatmentDetails.treatmentInstance.occurrence)} />
            </ListItem>
          </List>
        </Box>
        <Box mb={2} className="reschedule-dateInput">
          <Typography variant="h6" gutterBottom>
            Reschedule Date
          </Typography>
          <KeyboardDateSelector
            name="to"
            value={componentState.selectedDate}
            onChange={handleDateChange}
            disabled={!isMaster}
            minDate={new Date(treatmentDetails.minimumPossibleDate)}
            maxDate={new Date(treatmentDetails.maximumPossibleDate)}
            maxDateMessage={errorsLocale.getMsg('treatmentInstanceDatePastNextOccurenceOrEndDate')}
            errorProps={getErrorProps(validationErrors.selectedDate)}
          />
        </Box>
        <Box mb={2}>
          <Typography variant="h6" gutterBottom>
            Rescheduling reason
          </Typography>
          <Box py={1}>
            <DropdownFilter
              dropdownId="reschedulingReason"
              fullWidth
              label="Standard"
              disabled={componentState.disableFields}
              data={componentState.movedReasons}
              filterKey={'movedReason'}
              filterValue={componentState.movedReason}
              onChange={(name, value) => setComponentState({ ...componentState, movedReason: value })}
            />
          </Box>
          <Box className="rescheduleVisit-textField" pt={2}>
            <Typography variant="h6" gutterBottom>
              Risk assessment
            </Typography>
            <TextField
              required
              label={locale.getMsg('riskAssessment')}
              disabled={componentState.disableFields}
              placeholder={`${locale.getMsg('riskAssessment')} *`}
              multiline
              value={componentState.riskAssesment}
              error={componentState.riskAssesment.length > riskMaxLength}
              helperText={errorsLocale.getMsg('validationMaxLength', [`${riskMaxLength}`])}
              onChange={e => setComponentState({ ...componentState, riskAssesment: e.target.value })}
            />
          </Box>
        </Box>
      </div>
    );
  };

  const actionButtons = () => {
    return (
      <div className="sidePanel-footer">
        <div className="sidePanel-buttons">
          <div className="sidePanel-BackButton">
            <Button
              variant="contained"
              color="secondary"
              onClick={() => {
                setActiveUnassignedTreatmentForReschedule(null);
              }}
            >
              Back
            </Button>
          </div>
          <div className="sidePanel-saveButton">
            <Button
              variant="contained"
              color="primary"
              disabled={shouldDisableButton()}
              onClick={recheduleUnassignedTreatmentInstance}
            >
              Save
            </Button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <section className="side-panel-content-wrapper">
      <SwipeableDrawer
        anchor="right"
        open={activeUnassignedTreatmentForReschedule !== null}
        onOpen={() => {}}
        onClose={() => setActiveUnassignedTreatmentForReschedule(null)}
      >
        <div className="sidePanel-wrapper" style={{ width: '350px' }} id="reschedule-side-panel">
          {headerInfo()}
          {mainContent()}
          {isMaster && actionButtons()}
        </div>
      </SwipeableDrawer>
    </section>
  );
}
